// src/context/persona/PersonaModal.jsx
//
// v198 — ONE modal for every persona action (decision: a focused container; the page behind is inert). It turns
// the page's `flow` (see PersonasView) into a WizardModal: title, subtitle, the step bar, and the body of the
// current step. It owns three things only: the step list, the "close without saving?" question (a nested
// ConfirmDialog — Escape belongs to it while open, modalStack.js) and the leave guard while dirty. Every other
// state is the page's (`flow`, the job, the proposals) or the step's (the editor's draft).
//
// Flows → steps (decisions 4–6): propose [describe, proposals] · method [method] · manual [method?, identity,
// profile, needs] · aiCreate / generating / review (create) [method?, brief, generating, review] · personalize /
// generating / review (personalize) [instructions, generating, review] · edit (no bar). "method?" = only when the
// process started from the chooser. While "Generazione" is shown and the page's ONE job completes with a
// proposal, the modal moves to "Revisione" by itself.
//
// ⚠️ The dirty flag and the wizard step are stored WITH the flow they were reported for, and read back only while
// that flow is still the current one. A reset effect would run AFTER the step's own first report (parent effects
// run after the children's), so a review — dirty from its first render, an unsaved AI proposal — would read clean.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WizardModal from '../../components/ui/WizardModal.jsx';
import ConfirmDialog from '../../admin/components/ConfirmDialog.jsx';
import useLeaveGuard from '../brandVoice/useLeaveGuard.js';
import PersonaEditor from './PersonaEditor.jsx';
import NewPersonaPanel from './NewPersonaPanel.jsx';
import ProposeStep from './assist/ProposeStep.jsx';
import ProposalsStep from './assist/ProposalsStep.jsx';
import CreatePersonaForm from './assist/CreatePersonaForm.jsx';
import PersonalizeControl from './assist/PersonalizeControl.jsx';
import GeneratingStep from './assist/GeneratingStep.jsx';
import { Plus } from 'lucide-react';
import { ReactComponent as UserTargetIcon } from '../../assets/icons/user-target.svg';
import { ReactComponent as UsersTargetIcon } from '../../assets/icons/users-target.svg';
import { COLORS, RADII } from '../../constants/theme.js';
import { ghostBtn } from './personaStyles.js';

// The "+" on the new-persona icon: a small accent disc on the tile's lower-right edge.
const PLUS_BADGE = {
  position: 'absolute', right: '-2px', bottom: '-2px', width: '16px', height: '16px', borderRadius: RADII.pill,
  background: COLORS.accent, color: COLORS.surface, border: `2px solid ${COLORS.surface}`, boxSizing: 'border-box',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const jobKindOf = (flow) => {
  if (flow.kind === 'aiCreate') return 'create';
  if (flow.kind === 'personalize') return 'personalize';
  if (flow.kind === 'generating') return flow.jobKind;
  if (flow.kind === 'review') return flow.job.kind;
  return null;
};

/** The step keys of a flow (decision 4). `wizardStep` = the manual editor's current step. */
export function stepsFor(flow, wizardStep) {
  const lead = flow.origin === 'chooser' ? ['method'] : [];
  switch (flow.kind) {
    case 'propose': return { keys: ['describe', 'proposals'], current: flow.step };
    case 'method': return { keys: ['method'], current: 'method' };
    case 'manual': return { keys: [...lead, 'identity', 'profile', 'needs'], current: wizardStep || 'identity' };
    case 'edit': return { keys: [], current: null };
    default: {
      const kind = jobKindOf(flow);
      let current = kind === 'create' ? 'brief' : 'instructions';
      if (flow.kind === 'generating') current = 'generating';
      else if (flow.kind === 'review') current = 'review';
      return kind === 'create'
        ? { keys: [...lead, 'brief', 'generating', 'review'], current }
        : { keys: ['instructions', 'generating', 'review'], current };
    }
  }
}

/** Identifies one flow instance: a new key = a new process, which starts clean. */
const keyOf = (flow) => (flow ? `${flow.kind}:${flow.personaId || ''}:${flow.job ? flow.job.job_id : ''}` : '');

/** A value reported by the step, honoured only while the flow it was reported for is current. */
function useFlowScoped(flowKey) {
  const keyRef = useRef(flowKey);
  keyRef.current = flowKey;
  const [stored, setStored] = useState({ key: '', value: null });
  const set = useCallback((value) => setStored({ key: keyRef.current, value }), []);
  return [stored.key === flowKey ? stored.value : null, set];
}

export default function PersonaModal({
  flow, setFlow, tenantId, canWrite, rows, labelOf,
  jobApi, startJob, lastBody, proposeState, setProposeState, onEnable,
  advanced, onAdvancedChange, onSaved, onClose,
}) {
  const { t } = useTranslation(['context', 'common']);
  const flowKey = keyOf(flow);
  const [dirtyValue, setDirty] = useFlowScoped(flowKey);
  const [wizardStep, setWizardStep] = useFlowScoped(flowKey);
  const [asking, setAsking] = useState(false);
  const open = Boolean(flow);
  const dirty = Boolean(dirtyValue);

  useLeaveGuard(open && dirty, t('personas.modal.leaveWarning'));

  // "Generazione" → "Revisione" when the page's job completes with a proposal.
  useEffect(() => {
    if (flow && flow.kind === 'generating' && jobApi.view === 'completed' && jobApi.job && jobApi.job.proposal) {
      setFlow({ kind: 'review', origin: flow.origin, job: jobApi.job });
    }
  }, [flow, jobApi.view, jobApi.job, setFlow]);

  // A closed modal never keeps a pending question for the next one.
  useEffect(() => { if (!open) setAsking(false); }, [open]);

  const requestClose = useCallback(() => {
    if (dirty) setAsking(true);
    else onClose();
  }, [dirty, onClose]);

  if (!flow) return null;

  const { keys, current } = stepsFor(flow, wizardStep);
  const steps = keys.map((key) => ({ key, label: t(`personas.wizard.steps.${key}`) }));
  const { title, subtitle, icon } = headingOf(t, flow, labelOf);
  const saved = (id, info) => onSaved({ flow, id, name: info && info.name });

  const backToProposals = flow.origin === 'proposals' && (flow.kind === 'aiCreate' || flow.kind === 'personalize') && (
    <button
      type="button" onClick={() => setFlow({ kind: 'propose', step: 'proposals' })}
      style={{ ...ghostBtn(false), marginBottom: '16px' }}
    >
      {t('personas.modal.backToProposals')}
    </button>
  );

  const startAndGenerate = async (body) => {
    const ok = await startJob(body);
    if (ok) setFlow({ kind: 'generating', origin: flow.origin, jobKind: body.kind });
  };

  const ctx = {
    flow, setFlow, tenantId, canWrite, rows, labelOf, jobApi, lastBody, proposeState, setProposeState, onEnable,
    advanced, onAdvancedChange, setDirty, setWizardStep, requestClose, saved, startAndGenerate, backToProposals,
  };

  return (
    <WizardModal
      open title={title} subtitle={subtitle} icon={icon} steps={steps} current={current}
      stepsLabel={t('personas.modal.stepsLabel')} closeLabel={t('personas.modal.close')}
      onRequestClose={requestClose}
    >
      {bodyOf(ctx)}
      <ConfirmDialog
        open={asking}
        onClose={() => setAsking(false)}
        onConfirm={() => { setAsking(false); setDirty(false); onClose(); }}
        title={t('personas.modal.discardTitle')}
        message={t('personas.modal.discardMessage')}
        confirmLabel={t('personas.modal.discardConfirm')}
        variant="danger"
      />
    </WizardModal>
  );
}

/** Title + subtitle of the modal for a flow (decision 4's table). */
// "Crea nuova Buyer persona" keeps ONE heading for its whole process (Metodo → manual wizard or AI brief →
// Generazione → Revisione): the add-user dialog's look, with the persona icon and a "+".
const NEW_PERSONA_ICON = (
  <>
    <UserTargetIcon width={20} height={20} focusable="false" />
    <span style={PLUS_BADGE}><Plus size={10} strokeWidth={3} /></span>
  </>
);

// "Suggerisci un Set di Buyer Persona": the same look, with several people in the target.
const PROPOSE_ICON = (
  <>
    <UsersTargetIcon width={20} height={20} focusable="false" />
    <span style={PLUS_BADGE}><Plus size={10} strokeWidth={3} /></span>
  </>
);

function headingOf(t, flow, labelOf) {
  const kind = jobKindOf(flow);
  const fromCreate = flow.kind === 'method' || flow.kind === 'manual' || flow.origin === 'chooser';
  if (fromCreate && kind !== 'personalize') {
    return { title: t('personas.create.title'), subtitle: t('personas.create.subtitle'), icon: NEW_PERSONA_ICON };
  }
  if (flow.kind === 'propose') {
    return { title: t('personas.assist.proposeTitle'), subtitle: t('personas.assist.proposeSubtitle'), icon: PROPOSE_ICON };
  }
  if (kind === 'create') return { title: t('personas.assist.createTitle'), subtitle: null };
  if (kind === 'personalize') {
    return {
      title: t('personas.assist.personalizeTitle'),
      subtitle: labelOf(flow.personaId || (flow.job && flow.job.persona_id)),
    };
  }
  if (flow.kind === 'edit') return { title: labelOf(flow.personaId), subtitle: flow.personaId };
  return { title: t('personas.create.title'), subtitle: null };
}

/** The body of the current step. */
function bodyOf(c) {
  const { flow, setFlow, tenantId, jobApi } = c;
  switch (flow.kind) {
    case 'propose':
      return flow.step === 'proposals' && c.proposeState.result ? (
        <ProposalsStep
          result={c.proposeState.result} jobBusy={jobApi.busy}
          onCreateFrom={(initial) => setFlow({ kind: 'aiCreate', origin: 'proposals', initial })}
          onPersonalize={(personaId) => setFlow({ kind: 'personalize', origin: 'proposals', personaId })}
          onEnable={c.onEnable}
          onBack={() => setFlow({ kind: 'propose', step: 'describe' })}
        />
      ) : (
        <ProposeStep
          tenantId={tenantId} value={c.proposeState}
          onChange={(v) => c.setProposeState((prev) => ({ ...prev, request: v.request, selected: v.selected }))}
          onResult={(result) => {
            c.setProposeState((prev) => ({ ...prev, result }));
            setFlow({ kind: 'propose', step: 'proposals' });
          }}
          onShowPrevious={c.proposeState.result ? () => setFlow({ kind: 'propose', step: 'proposals' }) : undefined}
        />
      );
    case 'method':
      return (
        <NewPersonaPanel
          aiBusy={jobApi.busy}
          onChooseAi={() => setFlow({ kind: 'aiCreate', origin: 'chooser', initial: {} })}
          onChooseManual={() => setFlow({ kind: 'manual', origin: 'chooser' })}
        />
      );
    case 'manual':
      return (
        <PersonaEditor
          tenantId={tenantId} personaId="" canWrite mode="create"
          existingIds={c.rows.map((r) => r.id)}
          existingNames={c.rows.map((r) => c.labelOf(r.id))}
          advanced={c.advanced} onAdvancedChange={c.onAdvancedChange}
          onStepChange={c.setWizardStep} onDirtyChange={c.setDirty}
          onClose={c.requestClose} onSaved={c.saved}
        />
      );
    case 'aiCreate':
      return (
        <>
          {c.backToProposals}
          <CreatePersonaForm
            tenantId={tenantId} initial={flow.initial || {}} busy={jobApi.busy}
            onStart={c.startAndGenerate} startError={jobApi.startError} onDismissStartError={jobApi.dismiss}
            advanced={c.advanced} onAdvancedChange={c.onAdvancedChange} onDirtyChange={c.setDirty}
          />
        </>
      );
    case 'personalize':
      return (
        <>
          {c.backToProposals}
          <PersonalizeControl
            tenantId={tenantId} personaId={flow.personaId} busy={jobApi.busy}
            onStart={c.startAndGenerate} startError={jobApi.startError} onDismissStartError={jobApi.dismiss}
            onDirtyChange={c.setDirty}
          />
        </>
      );
    case 'generating':
      return (
        <GeneratingStep
          job={jobApi.job} view={jobApi.view}
          onRetry={c.lastBody ? () => c.startAndGenerate(c.lastBody) : undefined}
        />
      );
    case 'review':
      return (
        <PersonaEditor
          key={`review-${flow.job.job_id}`}
          tenantId={tenantId}
          personaId={flow.job.kind === 'create' ? flow.job.suggested_id : flow.job.persona_id}
          canWrite={c.canWrite} mode="review" proposal={{ job: flow.job }}
          advanced={c.advanced} onAdvancedChange={c.onAdvancedChange} onDirtyChange={c.setDirty}
          onClose={c.requestClose} onSaved={c.saved}
          onProposalDropped={() => jobApi.dismiss()}
        />
      );
    case 'edit':
      return (
        <PersonaEditor
          key={`edit-${flow.personaId}`}
          tenantId={tenantId} personaId={flow.personaId} canWrite={c.canWrite} mode="edit"
          advanced={c.advanced} onAdvancedChange={c.onAdvancedChange} onDirtyChange={c.setDirty}
          onClose={c.requestClose} onSaved={c.saved}
        />
      );
    default:
      return null;
  }
}
